'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Loader2 } from 'lucide-react';

interface StepUpDialogProps {
  open: boolean;
  /** Called with the new open-state. Closing without verifying = aborted. */
  onOpenChange: (open: boolean) => void;
  /** Fired once the password re-verification succeeded. */
  onVerified: () => void;
}

/**
 * Step-up re-authentication dialog. Sensitive permissions (e.g.
 * `billing.manage`) are flagged `requires_step_up` server-side; the API
 * returns `{ code: 'STEP_UP_REQUIRED' }` until the user re-verifies their
 * password via `/api/auth/step-up` (valid 5 min). This dialog performs that
 * re-verification, then the caller retries the original action.
 *
 * Reusable across any sensitive action — pair with a `requestStepUp()`
 * promise helper that opens it and resolves on verify/cancel.
 */
export function StepUpDialog({ open, onOpenChange, onVerified }: StepUpDialogProps) {
  const t = useTranslations('admin.stepUp');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setPassword('');
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/step-up', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        reset();
        onVerified();
        onOpenChange(false);
      } else {
        setError(data?.code === 'INVALID_PASSWORD' ? t('wrongPassword') : t('genericError'));
      }
    } catch {
      setError(t('genericError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (loading) return;
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
              {t('title')}
            </DialogTitle>
            <DialogDescription>{t('description')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-4">
            <Label htmlFor="step-up-password">{t('passwordLabel')}</Label>
            <Input
              id="step-up-password"
              type="password"
              autoFocus
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && <p className="text-sm text-rose-600">{error}</p>}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={loading}
              onClick={() => onOpenChange(false)}
            >
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={loading || !password}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {loading ? t('verifying') : t('verify')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
